// src/SignUp.js
import React, { useState } from "react";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { auth } from "./firebase";
import { db } from "./firebase";
import { collection, doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Filter } from 'bad-words';

function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    const filter = new Filter();
    filter.removeWords('hell', 'damn');
    // Username validation
    if (!username || username.length < 4) {
      setError("Username must be at least 4 characters.");
      setLoading(false);
      return;
    }
    // Profanity check
    if (filter.isProfane(username)) {
      setError("Username contains inappropriate language.");
      setLoading(false);
      return;
    }
    // Check username uniqueness
    const usernameRef = doc(collection(db, "usernames"), username.toLowerCase());
    const usernameSnap = await getDoc(usernameRef);
    if (usernameSnap.exists()) {
      setError("That username is already taken. Please choose another.");
      setLoading(false);
      return;
    }
    // Password complexity rules
    const complexityRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    if (!complexityRegex.test(password)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Minimal debug write to Firestore
        try {
          // console.log("Minimal debugcollection write succeeded");
          await setDoc(doc(collection(db, "debugcollection"), "debugdoc"), { test: true });
        } catch (err) {
          // console.error("Minimal debugcollection write failed:", err);
        }

      // Save username to Firestore (usernames collection)
      try {
        const usernameDocRef = doc(collection(db, "usernames"), username.toLowerCase());
        const usernameData = {
          username: username,
          createdAt: new Date().toISOString(),
          uid: userCredential.user.uid
        };
        // console.log("Writing to:", usernameDocRef.path, "with data:", usernameData);
        await setDoc(usernameDocRef, usernameData);
        // console.log("Username write succeeded");
      } catch (err) {
        // console.error("Username write failed:", err);
      }

      // Save profile info to users collection
      try {
        const userDocRef = doc(collection(db, "users"), userCredential.user.uid);
        const userData = {
          username: username,
          createdAt: new Date().toISOString(),
          isPremium: false,
          displayName: username
        };
        // console.log("Writing to:", userDocRef.path, "with data:", userData);
        await setDoc(userDocRef, userData);
        // console.log("User profile write succeeded");
      } catch (err) {
        // console.error("User profile write failed:", err);
      }

      await sendEmailVerification(userCredential.user);
      setSuccess("Account created! Please check your email to verify your account. If you don't see it within a few minutes, check your spam/junk folder.");
      setTimeout(() => {
        alert("Account created! Please check your email to verify your account. If you don't see it within a few minutes, check your spam/junk folder.");
        navigate("/login");
      }, 500);
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setUsername("");
    } catch (err) {
      console.error("Auth error:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("An account with this email already exists. Please log in or use a different email.");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
    }}>
      <div style={{
        width: 350,
        background: "#fff",
        borderRadius: 24,
        boxShadow: "0 8px 32px rgba(60,60,120,0.12)",
        padding: "2.5rem 2rem 2rem 2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}>
        {/* Optional: Illustration */}
        <svg width="64" height="64" style={{ marginBottom: 16 }} viewBox="0 0 24 24" fill="none">
          <rect x="2" y="4" width="20" height="16" rx="4" fill="#e4f8ec"/>
          <path d="M6 8h12M6 12h12M6 16h8" stroke="#1D665D" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <h2 style={{ margin: "0 0 8px 0", fontWeight: 700, color: "#222" }}>Register</h2>
        <p style={{ color: "#888", fontSize: 15, marginBottom: 16 }}>Please register to login.</p>
        <div style={{ 
          background: "#f0f9ff", 
          border: "1px solid #0ea5e9", 
          borderRadius: 8, 
          padding: "12px 16px", 
          marginBottom: 24,
          fontSize: 14,
          color: "#0c4a6e"
        }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
            <svg width="16" height="16" style={{ marginRight: 8, flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <strong>Email Verification Required</strong>
          </div>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4 }}>
            After signing up, check your email for a verification link. If you don't see it within a few minutes, check your spam/junk folder.
          </p>
        </div>
        <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ marginBottom: 16, width: "100%", position: "relative", maxWidth: 280 }}>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              placeholder="Username"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 16,
                outline: "none",
                background: "#f7f7f7",
                boxSizing: "border-box"
              }}
            />
          </div>
          <div style={{ marginBottom: 16, width: "100%", position: "relative", maxWidth: 280 }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="Email"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 16,
                outline: "none",
                background: "#f7f7f7",
                boxSizing: "border-box"
              }}
            />
          </div>
          <div style={{ marginBottom: 16, width: "100%", position: "relative", maxWidth: 280 }}>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="Password"
              style={{
                width: "100%",
                padding: "12px 40px 12px 16px",
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 16,
                outline: "none",
                background: "#f7f7f7",
                boxSizing: "border-box"
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                outline: "none"
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20C7 20 2.73 16.11 1 12c.74-1.61 1.81-3.06 3.06-4.31M9.88 9.88A3 3 0 0 1 12 9c1.66 0 3 1.34 3 3 0 .39-.08.76-.21 1.09" /><path d="M1 1l22 22" /></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12C2.73 7.89 7 4 12 4s9.27 3.89 11 8c-1.73 4.11-6 8-11 8S2.73 16.11 1 12z" /><circle cx="12" cy="12" r="3" /></svg>
              )}
            </button>
          </div>
          <div style={{ marginBottom: 16, width: "100%", position: "relative", maxWidth: 280 }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm Password"
              style={{
                width: "100%",
                padding: "12px 40px 12px 16px",
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 16,
                outline: "none",
                background: "#f7f7f7",
                boxSizing: "border-box"
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              tabIndex={-1}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                outline: "none"
              }}
              aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
            >
              {showConfirmPassword ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20C7 20 2.73 16.11 1 12c.74-1.61 1.81-3.06 3.06-4.31M9.88 9.88A3 3 0 0 1 12 9c1.66 0 3 1.34 3 3 0 .39-.08.76-.21 1.09" /><path d="M1 1l22 22" /></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12C2.73 7.89 7 4 12 4s9.27 3.89 11 8c-1.73 4.11-6 8-11 8S2.73 16.11 1 12z" /><circle cx="12" cy="12" r="3" /></svg>
              )}
            </button>
          </div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 8, marginBottom: 8, textAlign: 'center', maxWidth: 280 }}>
            By creating an account, you agree to our{' '}
            <a href="/terms" style={{ color: '#1D665D', textDecoration: 'underline' }}>Terms of Service</a> and{' '}
            <a href="/privacy" style={{ color: '#1D665D', textDecoration: 'underline' }}>Privacy Policy</a>.
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{
              width: 280,
              padding: "12px",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 17,
              marginTop: 8,
              marginBottom: 8,
              boxShadow: "0 2px 8px rgba(30,153,67,0.08)"
            }}>
            Sign Up
          </button>
        </form>
        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
        {success && <p style={{ color: "green", marginTop: 10 }}>{success}</p>}
        <p style={{ color: "#888", fontSize: 15, marginTop: 18 }}>
          Already have an account?{" "}
          <a href="/login" style={{ color: "#1D665D", fontWeight: 600, textDecoration: "none" }}>Sign In</a>
        </p>
      </div>
    </div>
  );
}

export default SignUp;